import { useState, useEffect } from "react";
import { X, Bell, BellOff, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { useAuth } from "@/contexts/AuthContext";

export function NotificationPrompt() {
  const { user } = useAuth();
  const { isSupported, isSubscribed, permission, subscribe, showNotification } = usePushNotifications();
  const [showPrompt, setShowPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Only show for logged-in users
    if (!user) return;

    // Check if prompt was dismissed recently (within 30 days)
    const dismissedAt = localStorage.getItem("notification_prompt_dismissed");
    if (dismissedAt) {
      const dismissedDate = new Date(dismissedAt);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 30) {
        setDismissed(true);
        return;
      }
    }

    // Show prompt after user has been on the page for a bit
    const timer = setTimeout(() => {
      if (isSupported && !isSubscribed && permission !== "denied") {
        setShowPrompt(true);
      }
    }, 10000); // 10 seconds after page load

    return () => clearTimeout(timer);
  }, [user, isSupported, isSubscribed, permission]);

  // Schedule monthly reminder check
  useEffect(() => {
    if (!isSubscribed || !user) return;

    const checkMonthlyReminder = () => {
      const lastDiagnosticMonth = localStorage.getItem("last_diagnostic_month");
      const now = new Date();
      const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      
      // If last diagnostic was not this month, show reminder
      if (lastDiagnosticMonth && lastDiagnosticMonth !== currentMonth) {
        const dayOfMonth = now.getDate();
        // Show reminder after day 5 of the month
        if (dayOfMonth >= 5) {
          const reminderShown = localStorage.getItem(`reminder_shown_${currentMonth}`);
          if (!reminderShown) {
            showNotification("Seu diagnóstico mensal está pronto", {
              body: "Atualize os dados do mês para manter seu Sentinela vigilante.",
              tag: "monthly-reminder",
              data: { url: "/diagnostico" },
            });
            localStorage.setItem(`reminder_shown_${currentMonth}`, "true");
          }
        }
      }
    };

    // Check on mount and every hour
    checkMonthlyReminder();
    const interval = setInterval(checkMonthlyReminder, 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isSubscribed, user, showNotification]);

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem("notification_prompt_dismissed", new Date().toISOString());
  };

  const handleEnable = async () => {
    const success = await subscribe();
    if (success) {
      setShowPrompt(false);
      // Show welcome notification
      await showNotification("Alertas ativados!", {
        body: "Você receberá lembretes mensais para atualizar seu diagnóstico.",
        tag: "welcome-notification",
      });
    }
  };

  if (!showPrompt || dismissed || isSubscribed || !user) {
    return null;
  }

  if (permission === "denied") {
    return null;
  }

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-sm animate-in slide-in-from-bottom-4 duration-300">
      <Card className="border-primary/20 bg-card shadow-xl">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Calendar className="h-5 w-5 text-primary" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-semibold text-foreground text-sm">
                    Lembrete Mensal
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    "Seu diagnóstico mensal está pronto" — todo mês, automaticamente
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 -mt-1 -mr-1"
                  onClick={handleDismiss}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex gap-2 mt-3">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  onClick={handleDismiss}
                >
                  <BellOff className="h-4 w-4 mr-1.5" />
                  Depois
                </Button>
                <Button
                  size="sm"
                  className="flex-1"
                  onClick={handleEnable}
                >
                  <Bell className="h-4 w-4 mr-1.5" />
                  Ativar
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}